import { useCallback, useState } from 'react';

import type { KeyboardEvent } from 'react';
import type { TpOptionsMenuRowEntry, TpOptionsSubmenuId } from '../types';
import type { SubmenuState, UseTpOptionsMenuResult } from './types';

export default function useTpOptionsMenu(): UseTpOptionsMenuResult {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [submenu, setSubmenu] = useState<SubmenuState>(null);

  const openMenu = useCallback((element: HTMLElement) => setAnchorEl(element), []);
  const closeMenu = useCallback(() => {
    setSubmenu(null);
    setAnchorEl(null);
  }, []);
  const openSubmenu = useCallback(
    (id: TpOptionsSubmenuId, element: HTMLElement, focusOnOpen: boolean) =>
      setSubmenu({ id, anchorEl: element, focusOnOpen }),
    [],
  );
  const closeSubmenu = useCallback(() => setSubmenu(null), []);

  const handleListKeyDown = useCallback(
    (event: KeyboardEvent<HTMLUListElement>) => {
      if (event.key !== 'Tab') return;
      event.preventDefault();
      closeMenu();
    },
    [closeMenu],
  );

  const handleRowKeyDown = useCallback(
    (event: KeyboardEvent<HTMLLIElement>, entry: TpOptionsMenuRowEntry) => {
      if (event.key !== 'ArrowLeft' || !entry.submenuId || entry.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      openSubmenu(entry.submenuId, event.currentTarget, true);
    },
    [openSubmenu],
  );

  const handleSubmenuKeyDown = useCallback(
    (event: KeyboardEvent<HTMLUListElement>) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        closeMenu();
        return;
      }
      if (event.key !== 'ArrowRight' && event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      const rowElement = submenu?.anchorEl;
      setSubmenu(null);
      rowElement?.focus();
    },
    [closeMenu, submenu],
  );

  return {
    anchorEl,
    openMenu,
    closeMenu,
    submenu,
    openSubmenu,
    closeSubmenu,
    handleListKeyDown,
    handleRowKeyDown,
    handleSubmenuKeyDown,
  };
}
